import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mui/material';
import { COLORS } from '../../../constants/radar';
import {
  ModalOverlay,
  ModalContent,
  Form,
  Field,
  Label,
  ButtonGroup,
} from './styles';

const Modal = ({
  isOpen,
  onClose,
  onSubmit,
  onCancel,
  title,
  children,
  submitText = 'Save',
  cancelText = 'Cancel',
  width,
  submitDisabled,
  cancelDisabled,
  maxWidth,
  onKeyDown,
  disableOverlayClose = false,
  hideSubmit = false, // read-only dialogs: only the cancel/close button
}) => {
  const modalContentRef = useRef(null);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();

    // Check if any child modal is open INSIDE this modal (like country creation modal or confirmation modal)
    // We only want to block if there's a modal that is a descendant of this modal's content
    if (modalContentRef.current) {
      const childMuiModals = modalContentRef.current.querySelectorAll('.MuiModal-root');
      const childCustomModalOverlays = modalContentRef.current.querySelectorAll('[data-modal-overlay="true"]');

      if (childMuiModals.length > 0 || childCustomModalOverlays.length > 0) {
        console.log('Modal: Child modal is open inside this modal, preventing form submission');
        return;
      }
    }

    // Also check for confirmation modals that might be rendered as siblings (portaled outside)
    // These are identified by data-modal-overlay="true" and should block submission
    const confirmationModals = document.querySelectorAll('div[data-modal-overlay="true"][data-confirmation-modal="true"]');
    if (confirmationModals.length > 0) {
      console.log('Modal: Confirmation modal detected, preventing form submission');
      return;
    }

    // Check if the form submission was triggered by Enter key in a selector that might create new items
    const activeElement = document.activeElement;
    const isInAutocomplete = activeElement?.closest('.MuiAutocomplete-root');
    const isInCountrySelector = activeElement?.closest('[data-testid="country-selector"]');

    if (isInAutocomplete && isInCountrySelector) {
      // Don't submit the form if we're in a CountrySelector that might be creating a new country
      const inputValue = activeElement?.value?.trim();
      if (inputValue) {
        // Let the CountrySelector handle the Enter key instead
        console.log('Modal: Preventing submission - in country selector');
        return;
      }
    }

    onSubmit(e);
  };

  const handleCancel = (e) => {
    e.preventDefault();
    if (onCancel) {
      onCancel(e);
    } else {
      onClose(e);
    }
  };

  return (
    <ModalOverlay onClick={disableOverlayClose ? undefined : onClose}>
      <ModalContent
        ref={modalContentRef}
        onClick={e => e.stopPropagation()}
        onKeyDown={onKeyDown}
        width={width}
        maxWidth={maxWidth}
      >
        <h2>{title}</h2>
        <Form onSubmit={handleSubmit} noValidate>
          {children}
          <ButtonGroup>
            <Button
              type="button"
              variant="outlined"
              onClick={handleCancel}
              disabled={cancelDisabled}
              sx={{
                textTransform: 'none',
                backgroundColor: COLORS.BACKGROUND,
                color: COLORS.PRIMARY,
                padding: '8px 16px',
                fontSize: '14px',
                border: 'none',
                '&:hover': {
                  backgroundColor: COLORS.HOVER,
                  border: 'none'
                }
              }}
            >
              {cancelText}
            </Button>
            {!hideSubmit && <Button
              type="submit"
              variant="contained"
              disabled={submitDisabled}
              sx={{
                textTransform: 'none',
                backgroundColor: COLORS.PRIMARY,
                padding: '8px 16px',
                fontSize: '14px',
                '&:hover': {
                  backgroundColor: COLORS.HOVER
                }
              }}
            >
              {submitText}
            </Button>}
          </ButtonGroup>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  submitText: PropTypes.string,
  cancelText: PropTypes.string,
  width: PropTypes.string,
  submitDisabled: PropTypes.bool,
  cancelDisabled: PropTypes.bool,
  maxWidth: PropTypes.string,
  onKeyDown: PropTypes.func,
  disableOverlayClose: PropTypes.bool,
  hideSubmit: PropTypes.bool,
};

export { Field, Label };
export default Modal; 